import React from "react";
import { useTranslation } from "react-i18next";
import { Badge, Card, Icon } from "@/ds";

/**
 * A reference market range, with its sources (DANY.md §2.8, DY2.2 piece 3).
 *
 * **This card has no button, and that is the design.** Every other card Dany
 * renders ends in an action; this one deliberately cannot. DANY.md §2.8 says the
 * amount is never auto-filled — the FixyBee types it into the quote — so a
 * "use this price" control would be the one thing the whole feature is built to
 * avoid. What the card offers instead is a range, where it came from, and a
 * fixed disclaimer that the price is theirs.
 *
 * The sources open in a new tab with `rel="noopener noreferrer"`: they are
 * third-party pages, and they are shown as citations, not as content we vouch
 * for. Their titles come from the search provider, so they are rendered as text
 * and never as markup.
 */
export default function DanyMarketPriceWidget({ data }) {
  const { t } = useTranslation();
  if (!data || data.denied || data.error) return null;

  const { price_min: low, price_max: high } = data;
  const sources = (data.sources || []).filter((s) => s && s.url).slice(0, 3);
  // `subject` is the line of the quote the range was actually looked up for
  // (N57); the category is the fallback it used to be the answer. A card that
  // says "Technology Services" over the price of installing Office is naming
  // the question we used to ask, not the one that was asked.
  const what = data.subject || data.specialty_name || data.category_name || "";
  const hasRange = low != null || high != null;

  const money = (value) =>
    `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const band =
    low != null && high != null && high !== low
      ? `${money(low)} – ${money(high)}`
      : money(low != null ? low : high);

  return (
    <Card variant="raised" padding="md" data-testid="dany-market-price">
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: "var(--space-2)", flexWrap: "wrap",
      }}>
        <span style={eyebrow}>
          <Icon name="search" size={12} style={{ color: "var(--dusk)" }} />
          {/* `region_label` names the town — "El Paso, TX · 79936" — and the
              bare `region` is the fallback for a ZIP the local table cannot
              name and for a whole-state search. Reading the raw ZIP was the
              defect: a range that varies by town has to say which town it
              priced, and five digits do not. */}
          {t("dany.market.header", { region: data.region_label || data.region || "" })}
        </span>
        {data.cached && (
          <Badge tone="neutral" data-testid="dany-market-cached">
            {t("dany.market.cached")}
          </Badge>
        )}
      </div>

      <p style={{
        marginTop: "var(--space-2)", fontSize: "var(--text-sm)",
        color: "var(--text-muted)",
      }}>{what}</p>

      {hasRange ? (
        <p style={{
          marginTop: "var(--space-2)", fontFamily: "var(--font-mono)",
          fontWeight: "var(--weight-semibold)", fontSize: "var(--text-2xl)",
          color: "var(--text-body)",
        }} data-testid="dany-market-range">{band}</p>
      ) : (
        <p style={{ ...body, color: "var(--text-muted)" }}
           data-testid="dany-market-no-range">
          {t("dany.market.noRange")}
        </p>
      )}

      {data.summary && (
        <p style={{ ...body }} data-testid="dany-market-summary">{data.summary}</p>
      )}

      {sources.length > 0 && (
        <div style={{ marginTop: "var(--space-4)" }}>
          <p style={{ ...eyebrow, marginBottom: "var(--space-2)" }}>
            {t("dany.market.sources")}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {sources.map((source) => (
              <a
                key={source.url}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                style={{
                  fontSize: "var(--text-xs)", color: "var(--text-accent)",
                  textDecoration: "none", overflowWrap: "anywhere",
                }}
                data-testid="dany-market-source"
              >
                <Icon name="arrow-right" size={11} />{" "}
                {source.title || source.url}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Fixed, always rendered, in both languages. DANY.md §2.8: the platform
          connects, it does not price — and a range read without this line is a
          range that reads as a recommendation. */}
      <p style={{
        marginTop: "var(--space-4)", paddingTop: "var(--space-3)",
        borderTop: "1px solid var(--border-subtle)",
        fontSize: "var(--text-xs)", color: "var(--text-subtle)",
        lineHeight: "var(--leading-relaxed)",
      }} data-testid="dany-market-disclaimer">
        {t("dany.market.disclaimer")}
      </p>
    </Card>
  );
}

const eyebrow = {
  display: "inline-flex", alignItems: "center", gap: 6,
  fontFamily: "var(--font-display)", fontSize: "var(--text-xs)",
  textTransform: "uppercase", letterSpacing: "var(--tracking-widest)",
  color: "var(--dany-meta)",
};

const body = {
  marginTop: "var(--space-2)", fontSize: "var(--text-sm)",
  color: "var(--text-muted)", lineHeight: "var(--leading-relaxed)",
  maxWidth: "var(--measure-body)",
};
